import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiClient } from "./api";
import { getReferenceValues } from "./referenceDataApi";
import type { ReferenceCategory } from "./referenceDataApi";
import { resolveReferenceLabel } from "../hooks/useReferenceValues";
import i18n from "../i18n";
import type { PlayerApiResponse } from "./players";
import type { PlayerStatsResponse } from "../types/playerStats";
import type {
  Competition,
  PaginatedResponse,
  Referee,
  Team,
  Venue,
} from "../types";

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

export type ExportFormat = "csv" | "json";

export interface ExportOptions {
  format: ExportFormat;
  filename?: string;
}

const ADMIN_LOCALES = ["en", "es"] as const;

const buildLocalizedNameColumns = (names?: Record<string, string> | null) =>
  ADMIN_LOCALES.reduce<Record<string, string>>((acc, locale) => {
    acc[`localized_name_${locale}`] = names?.[locale] ?? "";
    return acc;
  }, {});

/**
 * Convert array of objects to CSV string
 */
function arrayToCSV(data: any[], headers?: string[]): string {
  if (data.length === 0) return "";

  // Get headers from first object if not provided
  const cols = headers || Object.keys(data[0]);

  // Create header row
  const headerRow = cols.map((col) => `"${col}"`).join(",");

  // Create data rows
  const dataRows = data.map((row) => {
    return cols
      .map((col) => {
        const value = row[col];
        if (value === null || value === undefined) return "";
        if (typeof value === "object")
          return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Resolves reference-data codes (season, competition_stage, match_type,
 * venue_surface) to their current-language label for a real data export —
 * a client-facing deliverable must not mix machine codes ("round_of_16")
 * with human labels ("Round of 16") across old vs. new records. Falls back
 * to the raw stored string when it's deactivated or entirely unrecognized
 * (legacy free text) — same never-drop rule as every other reference-data
 * consumer, just for exported text instead of a select option.
 */
async function buildReferenceLabelResolver(
  category: ReferenceCategory,
): Promise<(code: string | null | undefined) => string> {
  const values = await getReferenceValues(category, true);
  const byCode = new Map(values.map((v) => [v.code, v]));
  return (code) => {
    if (!code) return "";
    const known = byCode.get(code);
    return known ? resolveReferenceLabel(known, i18n.language) : code;
  };
}

/**
 * Download data as file
 */
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const EXPORT_PAGE_SIZE = 100; // backend max page_size

async function fetchPagePass<T>(
  path: string,
  params: Record<string, unknown>,
): Promise<{ rows: T[]; total: number }> {
  const rows: T[] = [];
  let total = 0;
  let page = 1;
  for (;;) {
    const res = await apiClient.get<PaginatedResponse<T>>(path, {
      params: { ...params, page, page_size: EXPORT_PAGE_SIZE },
    });
    const items = res.items ?? [];
    if (page === 1) total = res.total ?? items.length;
    rows.push(...items);
    if (items.length === 0 || page >= res.total_pages) break;
    page += 1;
  }
  return { rows, total };
}

function dedupeBy<T>(rows: T[], idKey: keyof T & string): T[] {
  const seen = new Map<unknown, T>();
  for (const row of rows) {
    const id = row[idKey];
    if (!seen.has(id)) seen.set(id, row);
  }
  return [...seen.values()];
}

/**
 * Fetch every row of a paginated list endpoint (PaginatedResponse envelope).
 * Rows are deduplicated by `idKey` because some list endpoints page without a
 * stable sort and can repeat or skip rows across pages. If fewer unique rows
 * than the first page's `total` arrive, the pass is repeated once; if still
 * short, this throws rather than producing a silently short export file.
 */
export async function fetchAllPages<T>(
  path: string,
  idKey: keyof T & string,
  params: Record<string, unknown> = {},
): Promise<T[]> {
  let unique: T[] = [];
  let total = 0;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const pass = await fetchPagePass<T>(path, params);
    total = pass.total;
    unique = dedupeBy(pass.rows, idKey);
    if (unique.length >= total) return unique;
  }
  throw new Error(
    `Export of ${path} is incomplete: received ${unique.length} unique rows but the server reported ${total}.`,
  );
}

const MATCH_EXPORT_PAGE_SIZE = 100; // backend list_matches limit max

interface ExportMatchRow {
  match_id: string;
  competition?: { name?: string };
  home_team?: { name?: string };
  away_team?: { name?: string };
  venue?: { name?: string };
  referee?: { name?: string };
  match_date?: string;
  competition_stage?: string;
  status?: string;
  home_score?: number | string;
  away_score?: number | string;
}

/** /matches/ returns a bare array paged by skip/limit (max 100, default 50). */
async function fetchAllMatches(): Promise<ExportMatchRow[]> {
  const all: ExportMatchRow[] = [];
  for (let skip = 0; ; skip += MATCH_EXPORT_PAGE_SIZE) {
    const batch = await apiClient.get<ExportMatchRow[]>("/matches/", {
      params: { skip, limit: MATCH_EXPORT_PAGE_SIZE },
    });
    all.push(...batch);
    if (batch.length < MATCH_EXPORT_PAGE_SIZE) break;
  }
  return dedupeBy(all, "match_id");
}

// ============================================================================
// COMPETITION EXPORT
// ============================================================================

export async function exportCompetitions(
  options: ExportOptions = { format: "csv" },
) {
  const competitions = await fetchAllPages<Competition>(
    "/competitions/",
    "competition_id",
  );

  const exportData = competitions.map((comp) => ({
    competition_id: comp.competition_id,
    name: comp.name,
    ...buildLocalizedNameColumns(comp.i18n_names),
    short_name: comp.short_name,
    gender: comp.gender,
    country_name: comp.country_name,
  }));

  const filename =
    options.filename ||
    `competitions_${new Date().toISOString().split("T")[0]}.${options.format}`;

  if (options.format === "csv") {
    const csv = arrayToCSV(exportData);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(exportData, null, 2);
    downloadFile(json, filename, "application/json");
  }

  return { count: exportData.length, filename };
}

/**
 * Get competition export template
 */
export function downloadCompetitionTemplate(format: ExportFormat = "csv") {
  const template = [
    {
      name: "Example Competition",
      short_name: "EXCOMP",
      gender: "male",
      country_name: "Spain",
    },
  ];

  const filename = `competition_template.${format}`;

  if (format === "csv") {
    const csv = arrayToCSV(template);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(template, null, 2);
    downloadFile(json, filename, "application/json");
  }
}

// ============================================================================
// VENUE EXPORT
// ============================================================================

export async function exportVenues(options: ExportOptions = { format: "csv" }) {
  const [venues, resolveSurfaceLabel] = await Promise.all([
    fetchAllPages<Venue>("/venues/", "venue_id"),
    buildReferenceLabelResolver("venue_surface"),
  ]);

  const exportData = venues.map((venue) => ({
    venue_id: venue.venue_id,
    name: venue.name,
    ...buildLocalizedNameColumns(venue.i18n_names),
    city: venue.city,
    country_name: venue.country_name,
    capacity: venue.capacity || "",
    surface: resolveSurfaceLabel(venue.surface),
  }));

  const filename =
    options.filename ||
    `venues_${new Date().toISOString().split("T")[0]}.${options.format}`;

  if (options.format === "csv") {
    const csv = arrayToCSV(exportData);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(exportData, null, 2);
    downloadFile(json, filename, "application/json");
  }

  return { count: exportData.length, filename };
}

export function downloadVenueTemplate(format: ExportFormat = "csv") {
  const template = [
    {
      name: "Example Stadium",
      city: "Madrid",
      country_name: "Spain",
      capacity: 80000,
      surface: "Natural Grass",
    },
  ];

  const filename = `venue_template.${format}`;

  if (format === "csv") {
    const csv = arrayToCSV(template);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(template, null, 2);
    downloadFile(json, filename, "application/json");
  }
}

// ============================================================================
// REFEREE EXPORT
// ============================================================================

export async function exportReferees(
  options: ExportOptions = { format: "csv" },
) {
  const referees = await fetchAllPages<Referee>("/referees/", "referee_id");

  const exportData = referees.map((ref) => ({
    referee_id: ref.referee_id,
    name: ref.name,
    country_name: ref.country_name,
    years_of_experience: ref.years_of_experience || "",
  }));

  const filename =
    options.filename ||
    `referees_${new Date().toISOString().split("T")[0]}.${options.format}`;

  if (options.format === "csv") {
    const csv = arrayToCSV(exportData);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(exportData, null, 2);
    downloadFile(json, filename, "application/json");
  }

  return { count: exportData.length, filename };
}

export function downloadRefereeTemplate(format: ExportFormat = "csv") {
  const template = [
    {
      name: "Example Referee",
      country_name: "Spain",
      years_of_experience: 10,
    },
  ];

  const filename = `referee_template.${format}`;

  if (format === "csv") {
    const csv = arrayToCSV(template);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(template, null, 2);
    downloadFile(json, filename, "application/json");
  }
}

// ============================================================================
// PLAYER EXPORT
// ============================================================================

export async function exportPlayers(
  options: ExportOptions = { format: "csv" },
) {
  const players = await fetchAllPages<PlayerApiResponse>(
    "/players/",
    "player_id",
  );

  const exportData = players.map((player) => ({
    player_id: player.player_id,
    name: player.name,
    ...buildLocalizedNameColumns(player.i18n_names),
    position: player.position,
    country_name: player.country_name ?? player.nationality,
    birth_date: player.birth_date,
    age: player.age || "",
    player_height: player.player_height || player.height || "",
    player_weight: player.player_weight || player.weight || "",
  }));

  const filename =
    options.filename ||
    `players_${new Date().toISOString().split("T")[0]}.${options.format}`;

  if (options.format === "csv") {
    const csv = arrayToCSV(exportData);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(exportData, null, 2);
    downloadFile(json, filename, "application/json");
  }

  return { count: exportData.length, filename };
}

export function downloadPlayerTemplate(format: ExportFormat = "csv") {
  const template = [
    {
      name: "Example Player",
      position: "ST",
      country_name: "Spain",
      birth_date: "1995-05-15",
      player_height: 180,
      player_weight: 75,
    },
  ];

  const filename = `player_template.${format}`;

  if (format === "csv") {
    const csv = arrayToCSV(template);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(template, null, 2);
    downloadFile(json, filename, "application/json");
  }
}

export function downloadPlayerWithTeamTemplate(format: ExportFormat = "csv") {
  const template = [
    {
      name: "Example Player",
      position: "ST",
      country_name: "Spain",
      birth_date: "1995-05-15",
      player_height: 180,
      player_weight: 75,
      team_name: "Existing Team FC",
      jersey_number: 9,
    },
  ];

  const filename = `player_with_team_template.${format}`;

  if (format === "csv") {
    const csv = arrayToCSV(template);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(template, null, 2);
    downloadFile(json, filename, "application/json");
  }
}

// ============================================================================
// TEAM EXPORT
// ============================================================================

export async function exportTeams(options: ExportOptions = { format: "csv" }) {
  const teams = await fetchAllPages<Team>("/teams/", "team_id");

  const exportData = teams.map((team) => ({
    team_id: team.team_id,
    name: team.name,
    ...buildLocalizedNameColumns(team.i18n_names),
    short_name: team.short_name,
    country_name: team.country_name,
    gender: team.gender,
    founded_year: team.founded_year || "",
    stadium: team.stadium || "",
    manager_name: team.manager?.name || "",
  }));

  const filename =
    options.filename ||
    `teams_${new Date().toISOString().split("T")[0]}.${options.format}`;

  if (options.format === "csv") {
    const csv = arrayToCSV(exportData);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(exportData, null, 2);
    downloadFile(json, filename, "application/json");
  }

  return { count: exportData.length, filename };
}

export function downloadTeamTemplate(format: ExportFormat = "csv") {
  const template = [
    {
      name: "Example FC",
      short_name: "EFC",
      country_name: "Spain",
      gender: "male",
      founded_year: 1900,
      stadium: "Example Stadium",
      manager_name: "John Doe",
    },
  ];

  const filename = `team_template.${format}`;

  if (format === "csv") {
    const csv = arrayToCSV(template);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(template, null, 2);
    downloadFile(json, filename, "application/json");
  }
}

// ============================================================================
// MATCH EXPORT
// ============================================================================

export async function exportMatches(
  options: ExportOptions = { format: "csv" },
) {
  const [matches, resolveStageLabel] = await Promise.all([
    fetchAllMatches(),
    buildReferenceLabelResolver("competition_stage"),
  ]);

  const exportData = matches.map((match) => ({
    match_id: match.match_id,
    competition_name: match.competition?.name || "",
    home_team_name: match.home_team?.name || "",
    away_team_name: match.away_team?.name || "",
    match_date: match.match_date,
    venue_name: match.venue?.name || "",
    referee_name: match.referee?.name || "",
    competition_stage: resolveStageLabel(match.competition_stage),
    status: match.status,
    home_score: match.home_score || "",
    away_score: match.away_score || "",
  }));

  const filename =
    options.filename ||
    `matches_${new Date().toISOString().split("T")[0]}.${options.format}`;

  if (options.format === "csv") {
    const csv = arrayToCSV(exportData);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(exportData, null, 2);
    downloadFile(json, filename, "application/json");
  }

  return { count: exportData.length, filename };
}

export function downloadMatchTemplate(format: ExportFormat = "csv") {
  const template = [
    {
      competition_name: "Example Competition",
      home_team_name: "Example FC",
      away_team_name: "Another FC",
      match_date: "2025-01-15",
      venue_name: "Example Stadium",
      referee_name: "Example Referee",
      competition_stage: "Regular Season",
    },
  ];

  const filename = `match_template.${format}`;

  if (format === "csv") {
    const csv = arrayToCSV(template);
    downloadFile(csv, filename, "text/csv;charset=utf-8;");
  } else {
    const json = JSON.stringify(template, null, 2);
    downloadFile(json, filename, "application/json");
  }
}

/**
 * Download bulk template with all models
 */
export function downloadBulkTemplate(format: ExportFormat = "csv") {
  if (format === "csv") {
    // CSV format with section markers
    const csvContent = `# COMPETITIONS
name,country_name,gender
Premier League,England,male
La Liga,Spain,female

# VENUES
name,city,country_name
Old Trafford,Manchester,England
Camp Nou,Barcelona,Spain

# REFEREES
name,country_name
Michael Oliver,England
Stéphanie Frappart,France

# PLAYERS
name,birth_date,player_height,player_weight,country_name,position
Erling Haaland,2000-07-21,194,88,Norway,ST
Alexia Putellas,1994-02-04,170,58,Spain,CM

# TEAMS
name,short_name,country_name,gender,founded_year,stadium,manager_name
Manchester City,MCI,England,male,1880,Etihad Stadium,Pep Guardiola
FC Barcelona,FCB,Spain,female,1970,Camp Nou,Jonatan Giráldez
`;

    downloadFile(
      csvContent,
      "bulk_import_template.csv",
      "text/csv;charset=utf-8;",
    );
  } else {
    // JSON format with nested structure
    const jsonData = {
      competitions: [
        {
          name: "Premier League",
          country_name: "England",
          gender: "male",
        },
        {
          name: "La Liga",
          country_name: "Spain",
          gender: "female",
        },
      ],
      venues: [
        {
          name: "Old Trafford",
          city: "Manchester",
          country_name: "England",
        },
        {
          name: "Camp Nou",
          city: "Barcelona",
          country_name: "Spain",
        },
      ],
      referees: [
        {
          name: "Michael Oliver",
          country_name: "England",
        },
        {
          name: "Stéphanie Frappart",
          country_name: "France",
        },
      ],
      players: [
        {
          name: "Erling Haaland",
          birth_date: "2000-07-21",
          player_height: 194,
          player_weight: 88,
          country_name: "Norway",
          position: "ST",
        },
        {
          name: "Alexia Putellas",
          birth_date: "1994-02-04",
          player_height: 170,
          player_weight: 58,
          country_name: "Spain",
          position: "CM",
        },
      ],
      teams: [
        {
          name: "Manchester City",
          short_name: "MCI",
          country_name: "England",
          gender: "male",
          founded_year: 1880,
          stadium: "Etihad Stadium",
          managers: [{ name: "Pep Guardiola" }],
        },
        {
          name: "FC Barcelona",
          short_name: "FCB",
          country_name: "Spain",
          gender: "female",
          founded_year: 1970,
          stadium: "Camp Nou",
          managers: [{ name: "Jonatan Giráldez" }],
        },
      ],
    };

    const json = JSON.stringify(jsonData, null, 2);
    downloadFile(json, "bulk_import_template.json", "application/json");
  }
}

// ============================================================================
// PLAYER STATS REPORT EXPORT (post-match)
// ============================================================================

export interface PlayerStatsExportOptions {
  filename?: string;
}

/**
 * Export per-player stats as CSV. One row per player, metric columns.
 */
export function exportPlayerStatsCSV(
  data: PlayerStatsResponse,
  options: PlayerStatsExportOptions = {},
): void {
  const rows = data.players.map((p) => ({
    player_id: p.player_id,
    player_name: p.player_name,
    team_id: p.team_id,
    jersey_number: p.jersey_number ?? "",
    position: p.position ?? "",
    is_starter: p.is_starter,
    minutes_played: p.minutes_played,
    passes_attempted: p.passes.attempted,
    passes_completed: p.passes.completed,
    pass_completion_pct: p.passes.completion_pct,
    shots_total: p.shots.total,
    shots_on_target: p.shots.on_target,
    goals: p.shots.goals,
    shot_conversion_pct: p.shots.conversion_pct,
    yellow_cards: p.cards.yellow,
    red_cards: p.cards.red,
    events_total: p.events_total,
  }));

  const csv = arrayToCSV(rows);
  const filename =
    options.filename ??
    `player-stats-${data.match_id}-${
      new Date().toISOString().split("T")[0]
    }.csv`;
  downloadFile(csv, filename, "text/csv;charset=utf-8;");
}

/**
 * Export a post-match player stats report as PDF using jspdf + jspdf-autotable.
 * Produces a titled report with match metadata, per-team totals tables, and
 * a full per-player breakdown.
 */
export function exportPlayerStatsPDF(
  data: PlayerStatsResponse,
  options: PlayerStatsExportOptions = {},
): void {
  const doc = new jsPDF({ orientation: "landscape" });

  // Title
  doc.setFontSize(16);
  doc.text("Post-Match Player Statistics Report", 14, 16);

  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Match ID: ${data.match_id}`, 14, 24);
  doc.text(
    `Generated: ${new Date(data.generated_at).toLocaleString()}`,
    14,
    30,
  );

  let yOffset = 38;

  // Per-team totals
  const teamIds = Object.keys(data.team_totals);
  if (teamIds.length > 0) {
    doc.setFontSize(12);
    doc.setTextColor(30);
    doc.text("Team Totals", 14, yOffset);
    yOffset += 6;

    autoTable(doc, {
      startY: yOffset,
      head: [
        [
          "Team",
          "Goals",
          "Pass Att.",
          "Pass Comp.",
          "Pass %",
          "Shots",
          "On Target",
          "Yellow",
          "Red",
        ],
      ],
      body: teamIds.map((tid) => {
        const t = data.team_totals[tid];
        return [
          tid,
          t.goals,
          t.passes.attempted,
          t.passes.completed,
          t.passes.completion_pct.toFixed(1) + "%",
          t.shots.total,
          t.shots.on_target,
          t.cards.yellow,
          t.cards.red,
        ];
      }),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [79, 70, 229] },
    });

    yOffset = (doc as any).lastAutoTable?.finalY
      ? (doc as any).lastAutoTable.finalY + 10
      : yOffset + 30;
  }

  // Per-player table
  doc.setFontSize(12);
  doc.setTextColor(30);
  doc.text("Per-Player Breakdown", 14, yOffset);
  yOffset += 6;

  autoTable(doc, {
    startY: yOffset,
    head: [
      [
        "Player",
        "Team",
        "#",
        "Pos",
        "Starter",
        "Min",
        "PA",
        "PC",
        "P%",
        "SH",
        "SOT",
        "G",
        "YC",
        "RC",
        "EVT",
      ],
    ],
    body: data.players.map((p) => [
      p.player_name,
      p.team_id,
      p.jersey_number ?? "",
      p.position ?? "",
      p.is_starter ? "Y" : "N",
      p.minutes_played,
      p.passes.attempted,
      p.passes.completed,
      p.passes.completion_pct.toFixed(1) + "%",
      p.shots.total,
      p.shots.on_target,
      p.shots.goals,
      p.cards.yellow,
      p.cards.red,
      p.events_total,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [99, 102, 241] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });

  const filename =
    options.filename ??
    `player-stats-${data.match_id}-${
      new Date().toISOString().split("T")[0]
    }.pdf`;
  doc.save(filename);
}
